import React, { useEffect, useRef, useState } from "react";
import {
    Typography,
    Box,
    Button,
    CircularProgress,
    Container,
    Stack,
    Dialog,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    TableBody,
    Chip,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "../../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import { MILLIS_TO_LOCALE } from "../../../../utilities/helpers/formatUnix";
import { ROLE, usePermissions } from "../../../../utils/permissions";
import GeneralInfoModal from "../../../UI/atoms/Popup/GeneralInfoModal";
import JSONPretty from "react-json-pretty";
import { SYNC_ACTIONS } from "../../../../constants/configuration.const";

const OpenSearchSync = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncInProgress, setIsSyncInProgress] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [syncHistoryList, setSyncHistoryList] = useState<any[]>([]);
    const { hasRole } = usePermissions();
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [deactivatedIdInfo, setDeactivatedIdInfo] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        fetchSyncStatus();
        fetchSyncHistory();
    }, []);

    useEffect(() => {
        if (isSyncInProgress && !intervalRef.current) {
            intervalRef.current = setInterval(() => {
                fetchSyncStatus();
                fetchSyncHistory();
            }, 15000);
        }

        if (!isSyncInProgress && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isSyncInProgress]);

    const fetchSyncStatus = async () => {
        try {
            const { data } = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/sync/is-in-progress`
            );
            setTimeout(() => setIsSyncInProgress(data?.data?.isInProgress), 0);
        } catch (error) {
            console.error("Error fetching sync status", error);
        }
    };

    const fetchSyncHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const { data } = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/sync/history`
            );
            if (data?.success) {
                setSyncHistoryList(data.data || []);
            }
        } catch (error) {
            console.error("Error fetching sync history", error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleSync = async () => {
        setIsModalOpen(false);
        setIsLoading(true);
        try {
            const { data } = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/metadata/sync-to-opensearch`
            );
            if (data?.success) {
                enqueueSnackbar(
                    "The metadata sync has been initiated successfully.",
                    { variant: "success" }
                );
                fetchSyncHistory();
                setIsSyncInProgress(true);
            } else {
                enqueueSnackbar(data?.message || "Sync failed.", {
                    variant: "error",
                });
            }
        } catch (error: any) {
            const message =
                error?.response?.data?.message || "Failed to sync metadata.";
            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusColor = (
        status: string
    ): "default" | "error" | "success" | "info" => {
        switch (status?.toLowerCase()) {
            case "in_progress":
                return "info";
            case "completed":
                return "success";
            case "failed":
                return "error";
            default:
                return "default";
        }
    };

    const closeInfoModal = () => {
        setShowInfoPopup(false);
    };
    const openInfoModal = (row: any) => {
        setShowInfoPopup(true);
        setDeactivatedIdInfo(row?.ids);
    };

    const isDeactivedRow = (row: any) => {
        if (row.action === SYNC_ACTIONS.DEACTIVATED_RESOURCE) {
            return true;
        }
        return false;
    };

    return (
        <Container maxWidth="md">
            <Box textAlign="center" py={5}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    OpenSearch Metadata Sync
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>
                    Ensure your OpenSearch index is refreshed with the latest
                    metadata from Brightcove.
                </Typography>
                {hasRole(ROLE.ADMIN) && (
                    <Stack alignItems="center" spacing={2}>
                        {isLoading ? (
                            <CircularProgress size={30} color="primary" />
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SyncIcon />}
                                onClick={() => setIsModalOpen(true)}
                                sx={{ px: 4, py: 1.5, fontWeight: "bold" }}
                            >
                                Sync Metadata
                            </Button>
                        )}
                    </Stack>
                )}
            </Box>

            <Dialog
                fullWidth
                maxWidth="xs"
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <Box p={3}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mb={2}
                    >
                        <WarningAmberIcon color="warning" />
                        <Typography variant="h6">Confirm Sync</Typography>
                    </Stack>
                    <Typography>
                        Are you sure you want to re-sync the OpenSearch
                        metadata?
                    </Typography>
                    <Typography color="error" fontSize="0.9rem" mt={1}>
                        Note: Syncing will remove all existing assets from
                        OpenSearch and reindex metadata from Brightcove.
                    </Typography>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={2}
                        mt={3}
                    >
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            color="error"
                            variant="contained"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSync}
                            color="primary"
                            variant="outlined"
                        >
                            Confirm
                        </Button>
                    </Stack>
                </Box>
            </Dialog>
            <Typography variant="h6" fontWeight="bold" mt={4} mb={2}>
                Metadata Sync History
            </Typography>
            {isLoadingHistory && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <CircularProgress size={24} />
                </Box>
            )}
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small" aria-label="sync history table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <h3>Triggered By</h3>
                            </TableCell>
                            <TableCell>
                                <h3>Action</h3>
                            </TableCell>
                            <TableCell>
                                <h3>Status</h3>
                            </TableCell>
                            <TableCell>
                                <h3>Started At</h3>
                            </TableCell>
                            <TableCell>
                                <h3>Total Assets</h3>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {syncHistoryList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No records found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            syncHistoryList.map((row) => (
                                <TableRow
                                    key={row.id}
                                    title={
                                        isDeactivedRow(row)
                                            ? "Click to view asset ids."
                                            : ""
                                    }
                                    onClick={
                                        isDeactivedRow(row)
                                            ? () => openInfoModal(row)
                                            : undefined
                                    }
                                    sx={{
                                        cursor: isDeactivedRow(row)
                                            ? "pointer"
                                            : "default",
                                        "&:hover": isDeactivedRow(row)
                                            ? {
                                                  backgroundColor:
                                                      "action.hover",
                                              }
                                            : {},
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2">
                                            {row.user || "Unknown"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {row.action ||
                                                SYNC_ACTIONS.SYNCED_DATA}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status}
                                            color={getStatusColor(row.status)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {MILLIS_TO_LOCALE(row.started_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {row.info || "-"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <GeneralInfoModal
                title="Asset Ids"
                isOpen={showInfoPopup}
                handleClose={closeInfoModal}
            >
                <JSONPretty
                    id="json-pretty"
                    data={deactivatedIdInfo}
                    style={{ fontSize: "0.8em" }}
                ></JSONPretty>
            </GeneralInfoModal>
        </Container>
    );
};

export default OpenSearchSync;
